/**
 * Sprint T-16.1 · Automation Center · IntentsTab.
 *
 * Notification intents registrados por el engine · status="pending" en T-16.
 * Read-only · el envío real es T-17.
 */
import React, { useMemo, useState } from "react";
import { Mail, ChevronLeft, ChevronRight, Info } from "lucide-react";
import { WidgetCard, EmptyState, LoadingState, CitationBadge } from "../advisor";
import { useNotificationIntents } from "../../hooks/useAutomation";
import { NotificationIntentCard } from "./shared/DomainCards";

const PAGE_SIZE = 25;

const CHANNELS = ["email", "sms", "whatsapp", "push", "webhook"];


export default function IntentsTab() {
  const [channelFilter, setChannelFilter] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState("pending");
  const [page, setPage] = useState(0);

  const q = useNotificationIntents({
    status: statusFilter || undefined,
    limit: PAGE_SIZE,
    skip: page * PAGE_SIZE,
  });
  const rawItems = q.data?.items || [];
  const total = q.data?.total || 0;

  const items = useMemo(() => {
    if (channelFilter.size === 0) return rawItems;
    return rawItems.filter((i) => channelFilter.has(i.channel));
  }, [rawItems, channelFilter]);

  const countsByChannel = useMemo(() => {
    const c = {};
    for (const it of rawItems) {
      c[it.channel] = (c[it.channel] || 0) + 1;
    }
    return c;
  }, [rawItems]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const toggleChannel = (ch) =>
    setChannelFilter((prev) => {
      const next = new Set(prev);
      if (next.has(ch)) next.delete(ch);
      else next.add(ch);
      return next;
    });

  return (
    <section className="space-y-4" data-testid="automation-intents-tab">
      <div
        data-testid="intents-info-banner"
        className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface-2)]/40 p-3 text-[11px] text-[var(--ag-text-dim)]"
      >
        <Info className="mr-1 inline h-3 w-3" />
        En Sprint T-16 los intents se registran con <code className="font-mono">status=pending</code>.
        El envío real (email · SMS · WhatsApp · push · webhook) se implementará en{" "}
        <strong>Sprint T-17</strong> · Notification Dispatcher.
      </div>

      <WidgetCard
        title="Filtros"
        description={`${total} intents totales · ${items.length} visibles`}
        citations={["/api/automation/notifications/intents"]}
        testId="widget-intents-filters"
      >
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1" data-testid="intents-filter-channels">
            <span className="text-[9px] uppercase text-[var(--ag-text-dim)]">Canal:</span>
            {CHANNELS.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`intents-filter-channel-${c}`}
                onClick={() => toggleChannel(c)}
                className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] ${
                  channelFilter.has(c)
                    ? "border-[var(--ag-gold)] bg-[var(--ag-gold)]/15 text-[var(--ag-gold)]"
                    : "border-[var(--ag-border)] hover:bg-[var(--ag-surface-2)]"
                }`}
              >
                {c}
                {countsByChannel[c] != null && (
                  <span className="rounded bg-[var(--ag-surface-2)] px-1 py-0 text-[9px]">
                    {countsByChannel[c]}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1" data-testid="intents-filter-status">
            <span className="text-[9px] uppercase text-[var(--ag-text-dim)]">Estado:</span>
            {["pending", "", "sent", "failed"].map((s) => (
              <button
                key={s || "all"}
                type="button"
                data-testid={`intents-filter-status-${s || "all"}`}
                onClick={() => {
                  setStatusFilter(s);
                  setPage(0);
                }}
                className={`rounded border px-1.5 py-0.5 text-[10px] ${
                  statusFilter === s
                    ? "border-[var(--ag-gold)] bg-[var(--ag-gold)]/15 text-[var(--ag-gold)]"
                    : "border-[var(--ag-border)] hover:bg-[var(--ag-surface-2)]"
                }`}
              >
                {s || "todos"}
              </button>
            ))}
          </div>
        </div>
      </WidgetCard>

      <WidgetCard
        title={`Intents · ${items.length}`}
        description={"Read-only · el T-17 Notification Dispatcher los procesará."}
        citations={["notification_intents collection"]}
        testId="widget-intents-list"
      >
        {q.isLoading && <LoadingState />}
        {q.error && <EmptyState message="No se pudo cargar." />}
        {q.data && items.length === 0 && (
          <EmptyState
            title="Sin intents"
            message="Aún no se ha registrado ninguna intención de notificación."
            testId="intents-empty"
          />
        )}
        {items.length > 0 && (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2" data-testid="intents-grid">
            {items.map((intent) => (
              <NotificationIntentCard key={intent.id} intent={intent} />
            ))}
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="mt-3 flex items-center justify-between text-xs" data-testid="intents-pagination">
            <span className="text-[var(--ag-text-dim)]">
              Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {total}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                data-testid="intents-page-prev"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] disabled:opacity-30"
              >
                <ChevronLeft className="h-3 w-3" /> Anterior
              </button>
              <span className="px-2 text-[10px] tabular-nums">
                pág {page + 1} / {totalPages}
              </span>
              <button
                type="button"
                data-testid="intents-page-next"
                onClick={() => setPage((p) => p + 1)}
                disabled={(page + 1) * PAGE_SIZE >= total}
                className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] disabled:opacity-30"
              >
                Siguiente <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
      </WidgetCard>

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["status=pending invariante T-16", "T-17 procesará envíos"]} />
      </footer>
    </section>
  );
}
